import { create } from 'zustand'
import type { CallRecord, Conversation, Message, MessageStatus, Participant } from '../types'
import type {
  Conversation as WireConversation,
  ConversationParticipant as WireConversationParticipant,
  LastMessagePreview,
  Message as WireMessage,
  ReactionAggregate,
} from '../services/messenger.wire'
import { usePresenceStore } from '@/features/presence/store/presence.store'
import { messengerService } from '../services/messenger.service'
import { trace } from '../services/trace'
import { unreadCountFor } from './unread-count'

/**
 * Reactive state for the messenger feature.
 *
 * **Two halves**, both live in the same store while the migration from mock
 * to real data is in flight:
 *
 *   1. **UI half** (pre-existing) — `conversations`, `broadcasts`, `calls`,
 *      `messagesByConversation` use the rich UI shapes from `types.ts`
 *      (`unreadCount`, `time`, `lastMessage`, `isOnline`, …). The dashboard
 *      sidebar still derives its live unread badge from this.
 *   2. **Wire half** (new) — `wireConversations`, `wireMessagesByConv`,
 *      `messageCursors`, `typingByConv`, `pendingSends`. Hydrated from
 *      `messengerApi` (REST) + the Phoenix `conversation:*` channel via
 *      `useMessengerSubscription` (W3). UI components migrate to consume
 *      this half one at a time in W5–W11; W13 removes the UI-half mock
 *      seeds.
 *
 * Wire-half selector shapes match the server contract from
 * `inmeet_client/docs/api-contracts.md → Messenger Module`.
 */

interface ConversationCursor {
  nextCursor: string | null
  hasMore: boolean
}

interface MessengerState {
  /* ---- UI half (legacy / unread badge) ---- */
  conversations: Conversation[]
  calls: CallRecord[]
  messagesByConversation: Record<string, Message[]>
  isHydrated: boolean

  /** Client-only "favourite" flag per conversation (no server column). Persisted
   *  to localStorage; projected onto `Conversation.isFavorite`. */
  favoriteIds: Set<string>
  /** Toggle a conversation's favourite state (persists + re-projects). */
  toggleFavorite: (conversationId: string) => void

  /* ---- Wire half (W4) ---- */
  /** Source of truth for conversation rows from the server. */
  wireConversations: Map<string, WireConversation>
  /** Display order (mirrors `lastMessageAt DESC` from the server). */
  wireConversationOrder: string[]
  wireConversationListCursor: ConversationCursor

  /** Per-conversation history. Oldest → newest. */
  wireMessagesByConv: Map<string, WireMessage[]>
  /** Per-conversation pagination cursor for the message thread. */
  messageCursors: Map<string, ConversationCursor>

  /**
   * Calls feed — `type='call'` messages across **every** conversation the
   * caller belongs to, newest-first. Seeded from
   * `messengerApi.listMessagesForMe({type: 'call'})` (W14) and kept fresh
   * by `appendWireMessage` filtering on `type === 'call'` so live
   * channel-pushed call rows appear without a separate subscription.
   */
  wireCalls: WireMessage[]
  wireCallsCursor: ConversationCursor

  /** `convId → Set<userId>` for typing indicators. Decays after 5s. */
  typingByConv: Map<string, Set<string>>

  /**
   * Group conversation ids I've been removed from (an admin's `removed`
   * system event naming me, or my own `left` landing from another device),
   * as caught live off the conversation channel. While a conversation id is
   * in here the open thread swaps its composer for a "no longer a member"
   * notice instead of the message input; history stays visible, sending
   * does not. Cleared if I'm added back.
   */
  removedFromConversationIds: Set<string>

  /**
   * Optimistic message inserts pending server ack. Keyed by `tempId`. UI
   * renders these prepended to the thread; `reconcilePendingSend` swaps
   * them with the server-issued row.
   */
  pendingSends: Map<string, WireMessage>

  /**
   * Ids of my DM messages that one of my broadcasts just delivered — the copies
   * `sendBroadcast` reported. Their live `message:new` carries no broadcast
   * flag (the recipient hears the same event), so the thread checks this set
   * as well as `viaBroadcast`, which only history reads set. Held as ids, not
   * patched onto the messages, so it holds whichever arrives first: the copy
   * or the send's response.
   */
  broadcastCopyIds: Set<string>

  /* ---- UI half mutations (unchanged) ---- */
  setConversations: (
    next:
      | Conversation[]
      | ((prev: Conversation[]) => Conversation[]),
  ) => void
  markRead: (conversationId: string) => void
  incrementUnread: (conversationId: string) => void
  /** Lazily load calls/messages from the service on first read. */
  hydrate: () => Promise<void>
  loadMessages: (conversationId: string) => Promise<Message[]>

  /* ---- Wire half mutations ---- */
  /** Insert or update a conversation row. Recomputes display order. */
  upsertWireConversation: (conv: WireConversation) => void
  /** Bulk replace from `messengerApi.listConversations`. Preserves cursor. */
  setWireConversationList: (
    items: WireConversation[],
    cursor: ConversationCursor,
    mode: 'replace' | 'append',
  ) => void
  /**
   * Drop a conversation locally — used by the optimistic Delete-Chat /
   * Block flows (CDT-6). The accompanying thread + cursors + typing
   * state get cleared so re-creating the same DM doesn't replay stale
   * messages. The conversation may still exist server-side (DM archive
   * just sets a flag); the channel subscription will re-attach if it
   * comes back via channel events.
   */
  removeWireConversation: (id: string) => void
  /**
   * Empty a thread locally while KEEPING the conversation in the list — the
   * optimistic half of Clear Chat. `keepIds` are the messages that survive the
   * clear server-side ("Keep starred messages"), so the local view matches what
   * the next history fetch will return instead of blanking and then repopulating.
   */
  clearWireMessages: (conversationId: string, keepIds?: readonly string[]) => void
  /**
   * Patch the caller's participant flags (`muted` / `pinned` / `archived`)
   * on a single conversation — CDT-6 right-click menu actions. No re-sort
   * (these flags don't affect `lastMessageAt` ordering).
   */
  patchWireParticipantFlags: (
    conversationId: string,
    callerId: string,
    patch: Partial<{ muted: boolean; pinned: boolean; archived: boolean }>,
  ) => void

  /** Prepend a freshly-arrived message (channel `message:new`). */
  appendWireMessage: (msg: WireMessage) => void
  /** Replace one message in-place (channel `message:updated`). */
  replaceWireMessage: (msg: WireMessage) => void
  /** Mark a message as deleted in the local list (channel `message:deleted`). */
  markWireMessageDeleted: (conversationId: string, messageId: string) => void
  /** Drop a message from THIS reader's copy of a thread. Nothing is retracted,
   *  so unlike a delete there is no placeholder left behind. */
  hideWireMessageForMe: (conversationId: string, messageId: string) => void
  /** Append older messages from cursor pagination. */
  prependOlderMessages: (
    conversationId: string,
    items: WireMessage[],
    cursor: ConversationCursor,
  ) => void
  /** Bulk seed a thread (initial REST hydrate). */
  seedWireMessages: (
    conversationId: string,
    items: WireMessage[],
    cursor: ConversationCursor,
  ) => void
  /** Merge a batch of messages into a thread by id (add new / replace existing
   *  incl. edits & soft-deletes), kept sorted oldest→newest. Used by the
   *  local-first cache hydrate + delta sync. Order-agnostic input. */
  upsertWireMessages: (conversationId: string, items: WireMessage[]) => void

  /**
   * Bulk seed (or append) the calls feed from
   * `messengerApi.listMessagesForMe({type: 'call'})`. `replace` is used by
   * the initial REST hydrate; `append` extends the tail on fetchMore.
   */
  setWireCallsList: (
    items: WireMessage[],
    cursor: ConversationCursor,
    mode: 'replace' | 'append',
  ) => void

  /** Apply a `reaction:added` channel event. */
  applyReactionAdded: (
    conversationId: string,
    messageId: string,
    userId: string,
    emoji: string,
    isCaller: boolean,
  ) => void
  /** Apply a `reaction:removed` channel event. */
  applyReactionRemoved: (
    conversationId: string,
    messageId: string,
    userId: string,
    emoji: string,
    isCaller: boolean,
  ) => void

  /** Apply a `read:updated` channel event — updates the matching participant. */
  applyReadUpdate: (
    conversationId: string,
    userId: string,
    lastReadAt: string,
  ) => void

  /** Apply a `delivered:updated` channel event — advances the participant's
   *  delivered high-water-mark (monotonic). Drives DELIVERED ticks (✓✓ grey). */
  applyDeliveredUpdate: (
    conversationId: string,
    userId: string,
    lastDeliveredAt: string,
  ) => void

  /** Seed participants' delivered/read cursors from the history read API, so
   *  ticks render correctly on load/reload. Monotonic (only advances). */
  seedCursors: (
    conversationId: string,
    cursors: { userId: string; lastDeliveredAt: string | null; lastReadAt: string | null }[],
  ) => void

  /** Apply a `typing` channel event — auto-decays after 5s. */
  markTyping: (conversationId: string, userId: string) => void

  /** I'm no longer a member of `conversationId` (removed, or left on another
   *  device). Idempotent. */
  markRemovedFrom: (conversationId: string) => void
  /** Undo `markRemovedFrom`: I was added back to the group. */
  clearRemovedFrom: (conversationId: string) => void

  /** Add an optimistic send (assigns a temp id at the call site). */
  addPendingSend: (msg: WireMessage) => void
  /** Server ack landed — replace temp message with real row. */
  reconcilePendingSend: (tempId: string, real: WireMessage) => void
  /** Roll back an optimistic send (REST failure). */
  rollbackPendingSend: (tempId: string) => void

  /** Record DM message ids a broadcast send delivered (see `broadcastCopyIds`). */
  markBroadcastCopies: (messageIds: string[]) => void

  /** Wipe wire state (logout). */
  resetWire: () => void

  /** Wipes back to the seed list. Used on logout. */
  reset: () => void
}

/** Auto-decay window for the `typing` indicator (server fans out at most
 *  every 3 s; we keep the indicator visible for 5 s after the last event). */
const TYPING_TTL_MS = 5_000

/**
 * Snapshot a message into the shape `WireConversation.lastMessage` holds.
 *
 * Every place that advances `lastMessageAt` has to advance this with it. They
 * used to move the timestamp alone, so a conversation floated to the top of the
 * list still showing the preview text and time of whatever the server sent when
 * the list was first fetched.
 */
function previewFromMessage(m: WireMessage): LastMessagePreview {
  return {
    id: m.id,
    senderId: m.senderId,
    type: m.type,
    body: m.body,
    createdAt: m.createdAt,
    deletedAt: m.deletedAt,
    attachmentKind: m.attachments?.[0]?.kind ?? null,
  }
}

/** Sort by `lastMessageAt` desc, falling back to `createdAt` for empty convs. */
function sortConversationIds(map: Map<string, WireConversation>): string[] {
  const rows = Array.from(map.values())
  rows.sort((a, b) => {
    const ax = a.lastMessageAt ?? a.createdAt
    const bx = b.lastMessageAt ?? b.createdAt
    return bx.localeCompare(ax)
  })
  return rows.map((r) => r.id)
}

/**
 * Update the reactions aggregate on a message in-place (returns a new ref).
 *
 * **Idempotent for the caller path** — this is what lets us safely do
 * optimistic updates AND still apply channel echoes for the same emoji.
 * The contract:
 *
 *   * `isCaller = true`, `delta = +1`: no-op if the caller already
 *     reacted with this emoji (`byMe` is true); otherwise bump count and
 *     set `byMe`.
 *   * `isCaller = true`, `delta = -1`: no-op if the caller wasn't
 *     reacting (`byMe` is false); otherwise decrement and clear `byMe`.
 *   * `isCaller = false`: always applies the delta, leaves `byMe`
 *     untouched. Other users' actions can't be deduped client-side
 *     without tracking userId-per-emoji sets — server makes sure each
 *     event corresponds to one real state transition.
 */
function applyReactionToMessage(
  msg: WireMessage,
  emoji: string,
  delta: 1 | -1,
  isCaller: boolean,
): WireMessage {
  const existing = msg.reactions ?? []
  const i = existing.findIndex((r) => r.emoji === emoji)

  if (delta === 1) {
    if (i === -1) {
      return {
        ...msg,
        reactions: [...existing, { emoji, count: 1, byMe: isCaller }],
      }
    }
    if (isCaller && existing[i].byMe) {
      // Caller's optimistic add already happened; channel echoed it back.
      return msg
    }
    const next = existing.slice()
    next[i] = {
      ...existing[i],
      count: existing[i].count + 1,
      byMe: isCaller ? true : existing[i].byMe,
    }
    return { ...msg, reactions: next }
  }

  // delta === -1
  if (i === -1) return msg
  if (isCaller && !existing[i].byMe) {
    // Caller's optimistic remove already cleared byMe; channel echoed it back.
    return msg
  }
  const next: ReactionAggregate[] = existing.slice()
  const newCount = Math.max(0, existing[i].count - 1)
  if (newCount === 0) {
    next.splice(i, 1)
  } else {
    next[i] = {
      ...existing[i],
      count: newCount,
      byMe: isCaller ? false : existing[i].byMe,
    }
  }
  return { ...msg, reactions: next }
}

/**
 * Module-level typing-decay scheduler so multiple `markTyping` calls for the
 * same `(conv, user)` debounce cleanly without per-store-instance refs.
 */
const typingTimers = new Map<string, ReturnType<typeof setTimeout>>()
const typingKey = (convId: string, userId: string): string => `${convId}|${userId}`

const initialWire = (): Pick<
  MessengerState,
  'wireConversations'
  | 'wireConversationOrder'
  | 'wireConversationListCursor'
  | 'wireMessagesByConv'
  | 'messageCursors'
  | 'wireCalls'
  | 'wireCallsCursor'
  | 'typingByConv'
  | 'pendingSends'
  | 'removedFromConversationIds'
  | 'broadcastCopyIds'
> => ({
  wireConversations: new Map(),
  wireConversationOrder: [],
  wireConversationListCursor: { nextCursor: null, hasMore: false },
  wireMessagesByConv: new Map(),
  messageCursors: new Map(),
  wireCalls: [],
  wireCallsCursor: { nextCursor: null, hasMore: false },
  typingByConv: new Map(),
  pendingSends: new Map(),
  removedFromConversationIds: new Set(),
  broadcastCopyIds: new Set(),
})

/** Favourites are a client-only concept (no server column) — persist the id set
 *  in localStorage so it survives reloads. Best-effort; never throws. */
const FAVORITES_KEY = 'instavc_chat_favorites'
function loadFavoriteIds(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY)
    if (!raw) return new Set()
    const arr = JSON.parse(raw) as unknown
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [])
  } catch {
    return new Set()
  }
}
function saveFavoriteIds(ids: Set<string>): void {
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify([...ids]))
  } catch {
    /* storage unavailable — favourites just won't persist */
  }
}

/**
 * The server says which groups I have left (`left: true`); the read-only state
 * the thread renders from follows it. Re-added (the flag gone) clears it.
 */
function syncLeft(current: Set<string>, convs: readonly WireConversation[]): { removedFromConversationIds?: Set<string> } {
  let next: Set<string> | null = null
  for (const c of convs) {
    if (c.left && !current.has(c.id)) (next ??= new Set(current)).add(c.id)
    else if (!c.left && c.kind === 'group' && current.has(c.id) && c.participants !== undefined) (next ??= new Set(current)).delete(c.id)
  }
  return next ? { removedFromConversationIds: next } : {}
}

export const useMessengerStore = create<MessengerState>((set, get) => ({
  /* ---- UI half (legacy slot; populated by `hydrate()` from messengerService
     until W13's full UI sweep replaces every consumer with wire-derived data).
     Empty by default — no MOCK seed. Sidebar unread-badge math still works
     because once `hydrate()` runs (or future wiring populates this from
     wire), the selectors below compute correctly. ---- */
  conversations: [],
  calls: [],
  messagesByConversation: {},
  isHydrated: false,

  favoriteIds: loadFavoriteIds(),
  toggleFavorite: (id) =>
    set((prev) => {
      const next = new Set(prev.favoriteIds)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      saveFavoriteIds(next)
      return { favoriteIds: next }
    }),

  /* ---- Wire half (W4) ---- */
  ...initialWire(),

  setConversations: (next) =>
    set((prev) => ({
      conversations:
        typeof next === 'function' ? next(prev.conversations) : next,
    })),

  markRead: (id) =>
    set((prev) => ({
      conversations: prev.conversations.map((c) =>
        c.id === id ? { ...c, unreadCount: 0 } : c,
      ),
    })),

  incrementUnread: (id) =>
    set((prev) => ({
      conversations: prev.conversations.map((c) =>
        c.id === id ? { ...c, unreadCount: c.unreadCount + 1 } : c,
      ),
    })),

  hydrate: async () => {
    if (get().isHydrated) return
    const [conversations, calls] = await Promise.all([
      messengerService.listConversations(),
      messengerService.listCalls(),
    ])
    set({ conversations, calls, isHydrated: true })
  },

  loadMessages: async (conversationId) => {
    const cached = get().messagesByConversation[conversationId]
    if (cached) return cached
    const fresh = await messengerService.getMessages(conversationId)
    set((prev) => ({
      messagesByConversation: { ...prev.messagesByConversation, [conversationId]: fresh },
    }))
    return fresh
  },

  /* ---- Wire half mutations ---- */

  upsertWireConversation: (conv) =>
    set((prev) => {
      const next = new Map(prev.wireConversations)
      next.set(conv.id, conv)
      return {
        wireConversations: next,
        wireConversationOrder: sortConversationIds(next),
        ...syncLeft(prev.removedFromConversationIds, [conv]),
      }
    }),

  removeWireConversation: (id) =>
    set((prev) => {
      if (!prev.wireConversations.has(id)) return {}
      const nextConvs = new Map(prev.wireConversations)
      nextConvs.delete(id)
      const nextMsgs = new Map(prev.wireMessagesByConv)
      nextMsgs.delete(id)
      const nextCursors = new Map(prev.messageCursors)
      nextCursors.delete(id)
      const nextTyping = new Map(prev.typingByConv)
      nextTyping.delete(id)
      // Drop any pending optimistic sends targeting this conversation.
      const nextPending = new Map(prev.pendingSends)
      for (const [tempId, msg] of nextPending) {
        if (msg.conversationId === id) nextPending.delete(tempId)
      }
      return {
        wireConversations: nextConvs,
        wireConversationOrder: sortConversationIds(nextConvs),
        wireMessagesByConv: nextMsgs,
        messageCursors: nextCursors,
        typingByConv: nextTyping,
        pendingSends: nextPending,
      }
    }),

  clearWireMessages: (conversationId, keepIds) =>
    set((prev) => {
      const keep = keepIds && keepIds.length > 0 ? new Set(keepIds) : null
      const existing = prev.wireMessagesByConv.get(conversationId)
      const kept = keep && existing ? existing.filter((m) => keep.has(m.id)) : []

      const nextMsgs = new Map(prev.wireMessagesByConv)
      if (kept.length > 0) nextMsgs.set(conversationId, kept)
      else nextMsgs.delete(conversationId)

      // Reset pagination too: the thread's `nextCursor` points at a message that
      // is no longer reachable, and leaving it would let a scroll-up page the
      // cleared history straight back in.
      const nextCursors = new Map(prev.messageCursors)
      nextCursors.delete(conversationId)

      const nextPending = new Map(prev.pendingSends)
      for (const [tempId, msg] of nextPending) {
        if (msg.conversationId === conversationId) nextPending.delete(tempId)
      }

      // Blank the list tile's preview so it doesn't keep showing a message the
      // user just cleared; the server's next list response agrees.
      const nextConvs = new Map(prev.wireConversations)
      const conv = nextConvs.get(conversationId)
      if (conv) {
        const newest = kept[kept.length - 1] ?? null
        nextConvs.set(conversationId, {
          ...conv,
          lastMessage: newest
            ? {
                id: newest.id,
                senderId: newest.senderId,
                type: newest.type,
                body: newest.body,
                createdAt: newest.createdAt,
                deletedAt: newest.deletedAt ?? null,
                attachmentKind: newest.attachments?.[0]?.kind ?? null,
              }
            : null,
        })
      }

      return {
        wireConversations: nextConvs,
        wireMessagesByConv: nextMsgs,
        messageCursors: nextCursors,
        pendingSends: nextPending,
      }
    }),

  patchWireParticipantFlags: (conversationId, callerId, patch) =>
    set((prev) => {
      const conv = prev.wireConversations.get(conversationId)
      if (!conv?.participants) return {}
      const idx = conv.participants.findIndex((p) => p.userId === callerId)
      if (idx < 0) return {}
      const nextParts = conv.participants.slice()
      nextParts[idx] = { ...nextParts[idx], ...patch }
      const nextConvs = new Map(prev.wireConversations)
      nextConvs.set(conversationId, { ...conv, participants: nextParts })
      // No order change — these flags don't affect `lastMessageAt` sort.
      return { wireConversations: nextConvs }
    }),

  setWireConversationList: (items, cursor, mode) =>
    set((prev) => {
      trace('store', `setWireConversationList (${mode})  +${items.length} conversations`, items.map((c) => ({ id: c.id.slice(0, 8), title: c.title, lastMessageAt: c.lastMessageAt })))
      const next = mode === 'replace'
        ? new Map<string, WireConversation>()
        : new Map(prev.wireConversations)
      for (const c of items) next.set(c.id, c)
      return {
        ...syncLeft(prev.removedFromConversationIds, items),
        wireConversations: next,
        wireConversationOrder: sortConversationIds(next),
        wireConversationListCursor: cursor,
      }
    }),

  appendWireMessage: (msg) =>
    set((prev) => {
      trace('store', `appendWireMessage  conv=${msg.conversationId.slice(0, 8)} id=${msg.id.slice(0, 12)} "${(msg.body ?? '').slice(0, 30)}"`)
      // Drop any pending temp row that matches (same convo + same body) so
      // we don't show duplicates after the server ack races the channel.
      const list = prev.wireMessagesByConv.get(msg.conversationId) ?? []
      if (list.some((m) => m.id === msg.id)) return {} // already present
      const nextList = [...list, msg]
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(msg.conversationId, nextList)

      // Bump the conversation's lastMessageAt + re-sort the list view.
      const conv = prev.wireConversations.get(msg.conversationId)
      const nextConvs = new Map(prev.wireConversations)
      if (conv) {
        nextConvs.set(msg.conversationId, {
          ...conv,
          lastMessageAt: msg.createdAt,
          lastMessage: previewFromMessage(msg),
        })
      }

      // W14 — fold call rows into the cross-conversation calls feed so the
      // calls-tab gets live updates without a dedicated subscription.
      const nextCalls =
        msg.type === 'call' && !prev.wireCalls.some((c) => c.id === msg.id)
          ? [msg, ...prev.wireCalls]
          : prev.wireCalls

      // The message landing is proof the sender is done typing it: don't
      // leave their indicator up for the rest of the TTL. Other senders'
      // typing state in this conversation is untouched. (A reconciled
      // own-message hits this too, but you never appear in your own
      // `typingByConv`, so it's a no-op there.)
      let nextTyping = prev.typingByConv
      if (msg.senderId) {
        const senderId = msg.senderId
        const typingSet = prev.typingByConv.get(msg.conversationId)
        if (typingSet?.has(senderId)) {
          const nextSet = new Set(typingSet)
          nextSet.delete(senderId)
          nextTyping = new Map(prev.typingByConv)
          if (nextSet.size === 0) nextTyping.delete(msg.conversationId)
          else nextTyping.set(msg.conversationId, nextSet)
        }
        const timerKey = typingKey(msg.conversationId, senderId)
        const timer = typingTimers.get(timerKey)
        if (timer) {
          clearTimeout(timer)
          typingTimers.delete(timerKey)
        }
      }

      return {
        wireMessagesByConv: nextMap,
        wireConversations: nextConvs,
        wireConversationOrder: sortConversationIds(nextConvs),
        wireCalls: nextCalls,
        typingByConv: nextTyping,
      }
    }),

  setWireCallsList: (items, cursor, mode) =>
    set((prev) => {
      const seen = new Set<string>()
      const merged: WireMessage[] = []
      const push = (m: WireMessage) => {
        if (seen.has(m.id)) return
        seen.add(m.id)
        merged.push(m)
      }
      if (mode === 'append') for (const m of prev.wireCalls) push(m)
      for (const m of items) push(m)
      if (mode === 'replace') {
        // Replace mode dropped the existing list above — `merged` is items only.
      }
      // Sort newest-first for the calls-tab. Stable on createdAt + id tiebreak.
      merged.sort((a, b) => {
        if (a.createdAt !== b.createdAt) return a.createdAt > b.createdAt ? -1 : 1
        return a.id > b.id ? -1 : 1
      })
      return { wireCalls: merged, wireCallsCursor: cursor }
    }),

  replaceWireMessage: (msg) =>
    set((prev) => {
      const list = prev.wireMessagesByConv.get(msg.conversationId)
      if (!list) return {}
      const i = list.findIndex((m) => m.id === msg.id)
      if (i === -1) return {}
      const nextList = list.slice()
      // Merge (not replace): signal-driven edits broadcast a partial message
      // (id/body/editedAt) — keep the existing reactions/attachments/etc.
      nextList[i] = { ...list[i], ...msg }
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(msg.conversationId, nextList)
      return { wireMessagesByConv: nextMap }
    }),

  hideWireMessageForMe: (conversationId, messageId) =>
    set((prev) => {
      const list = prev.wireMessagesByConv.get(conversationId)
      if (!list) return {}
      const nextList = list.filter((m) => m.id !== messageId)
      if (nextList.length === list.length) return {}
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(conversationId, nextList)

      // If it was the newest message, the chat-list tile has to fall back to
      // the one before it — leaving the tile quoting a message this reader can
      // no longer open is the same bug the retraction path had.
      const conv = prev.wireConversations.get(conversationId)
      if (!conv?.lastMessage || conv.lastMessage.id !== messageId) {
        return { wireMessagesByConv: nextMap }
      }
      const previous = nextList[nextList.length - 1]
      const nextConvs = new Map(prev.wireConversations)
      nextConvs.set(conversationId, {
        ...conv,
        lastMessage: previous
          ? {
              id: previous.id,
              senderId: previous.senderId,
              type: previous.type,
              body: previous.body,
              createdAt: previous.createdAt,
              deletedAt: previous.deletedAt,
              attachmentKind: previous.attachments?.[0]?.kind ?? null,
            }
          : null,
      })
      return { wireMessagesByConv: nextMap, wireConversations: nextConvs }
    }),

  markWireMessageDeleted: (conversationId, messageId) =>
    set((prev) => {
      const list = prev.wireMessagesByConv.get(conversationId)
      if (!list) return {}
      const i = list.findIndex((m) => m.id === messageId)
      if (i === -1) return {}
      const nextList = list.slice()
      // Match the wire contract: `deletedAt` set, body becomes the placeholder
      // the server returns from the next read.
      nextList[i] = {
        ...nextList[i],
        deletedAt: new Date().toISOString(),
        body: 'this message was deleted',
        // A retraction takes the attachments with it. Leaving them on the row
        // kept the photo or the file rendering under the placeholder — the
        // thing that was withdrawn still on screen — and left the chat-list
        // tile previewing it as "Photo" rather than as deleted.
        attachments: [],
      }
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(conversationId, nextList)

      // The chat-list tile carries its own copy of the last message, and it is
      // what the list renders from — so deleting the newest message updated
      // the thread and left the tile quoting text that no longer existed
      // anywhere, until something else refetched the conversation.
      const conv = prev.wireConversations.get(conversationId)
      if (!conv?.lastMessage || conv.lastMessage.id !== messageId) {
        return { wireMessagesByConv: nextMap }
      }
      const nextConvs = new Map(prev.wireConversations)
      nextConvs.set(conversationId, {
        ...conv,
        lastMessage: {
          ...conv.lastMessage,
          deletedAt: nextList[i].deletedAt,
          body: nextList[i].body,
        },
      })
      return { wireMessagesByConv: nextMap, wireConversations: nextConvs }
    }),

  prependOlderMessages: (conversationId, items, cursor) =>
    set((prev) => {
      const existing = prev.wireMessagesByConv.get(conversationId) ?? []
      const seen = new Set(existing.map((m) => m.id))
      const fresh = items.filter((m) => !seen.has(m.id))
      // Server returns newest-first per page; we render oldest→newest, so
      // reverse the page before prepending.
      const nextList = [...fresh.slice().reverse(), ...existing]
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(conversationId, nextList)
      const nextCursors = new Map(prev.messageCursors)
      nextCursors.set(conversationId, cursor)
      return { wireMessagesByConv: nextMap, messageCursors: nextCursors }
    }),

  seedWireMessages: (conversationId, items, cursor) =>
    set((prev) => {
      // Server gave us a newest-first page — reverse for rendering.
      const oldestFirst = items.slice().reverse()
      const existing = prev.wireMessagesByConv.get(conversationId) ?? []

      /**
       * Keep messages the server's page does not have YET.
       *
       * Messages are persisted asynchronously — socket -> Kafka -> storage —
       * while the socket delivers them live and immediately. So a page fetched
       * moments after delivery can legitimately not contain the newest
       * message, and this used to overwrite the thread with that page and
       * throw the message away.
       *
       * That is why only the FIRST message from a new contact went missing: it
       * arrives, the reader opens the thread, the seed lands a page that
       * predates it and wipes it. By the second message the thread is already
       * seeded, so appends simply stick.
       *
       * Only messages NEWER than the page's newest row survive. Anything older
       * that the server left out was left out deliberately — deleted, hidden,
       * or beyond the page — and resurrecting those would be a different bug.
       */
      const inPage = new Set(oldestFirst.map((m) => m.id))
      const newestInPage = oldestFirst.length
        ? oldestFirst[oldestFirst.length - 1].createdAt
        : null
      const survivors = existing.filter(
        (m) => !inPage.has(m.id) && (newestInPage === null || m.createdAt >= newestInPage),
      )

      const nextList = survivors.length
        ? [...oldestFirst, ...survivors].sort((a, b) =>
            a.createdAt !== b.createdAt ? (a.createdAt < b.createdAt ? -1 : 1) : a.id < b.id ? -1 : 1,
          )
        : oldestFirst

      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(conversationId, nextList)
      const nextCursors = new Map(prev.messageCursors)
      nextCursors.set(conversationId, cursor)
      return { wireMessagesByConv: nextMap, messageCursors: nextCursors }
    }),

  upsertWireMessages: (conversationId, items) =>
    set((prev) => {
      if (items.length === 0) return {}
      const list = prev.wireMessagesByConv.get(conversationId) ?? []
      const byId = new Map(list.map((m) => [m.id, m]))
      for (const m of items) {
        const existing = byId.get(m.id)
        // Merge so a delta edit/delete/reaction update lands on the cached row.
        byId.set(m.id, existing ? { ...existing, ...m } : m)
      }
      const merged = [...byId.values()].sort((a, b) =>
        a.createdAt !== b.createdAt ? (a.createdAt < b.createdAt ? -1 : 1) : a.id < b.id ? -1 : 1,
      )
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(conversationId, merged)

      const last = merged[merged.length - 1]
      const conv = prev.wireConversations.get(conversationId)
      const nextConvs = new Map(prev.wireConversations)
      if (conv && last && (!conv.lastMessageAt || last.createdAt > conv.lastMessageAt)) {
        nextConvs.set(conversationId, {
          ...conv,
          lastMessageAt: last.createdAt,
          lastMessage: previewFromMessage(last),
        })
      }
      return {
        wireMessagesByConv: nextMap,
        wireConversations: nextConvs,
        wireConversationOrder: sortConversationIds(nextConvs),
      }
    }),

  applyReactionAdded: (conversationId, messageId, _userId, emoji, isCaller) =>
    set((prev) => {
      const list = prev.wireMessagesByConv.get(conversationId)
      if (!list) return {}
      const i = list.findIndex((m) => m.id === messageId)
      if (i === -1) return {}
      const nextList = list.slice()
      nextList[i] = applyReactionToMessage(list[i], emoji, 1, isCaller)
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(conversationId, nextList)
      return { wireMessagesByConv: nextMap }
    }),

  applyReactionRemoved: (conversationId, messageId, _userId, emoji, isCaller) =>
    set((prev) => {
      const list = prev.wireMessagesByConv.get(conversationId)
      if (!list) return {}
      const i = list.findIndex((m) => m.id === messageId)
      if (i === -1) return {}
      const nextList = list.slice()
      nextList[i] = applyReactionToMessage(list[i], emoji, -1, isCaller)
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(conversationId, nextList)
      return { wireMessagesByConv: nextMap }
    }),

  applyReadUpdate: (conversationId, userId, lastReadAt) =>
    set((prev) => {
      const conv = prev.wireConversations.get(conversationId)
      if (!conv?.participants) return {}
      const i = conv.participants.findIndex((p) => p.userId === userId)
      if (i === -1) return {}
      const cur = conv.participants[i].lastReadAt
      if (cur && cur >= lastReadAt) return {} // monotonic
      const nextParts = conv.participants.slice()
      nextParts[i] = { ...nextParts[i], lastReadAt }
      const nextConvs = new Map(prev.wireConversations)
      nextConvs.set(conversationId, { ...conv, participants: nextParts })
      return { wireConversations: nextConvs }
    }),

  applyDeliveredUpdate: (conversationId, userId, lastDeliveredAt) =>
    set((prev) => {
      const conv = prev.wireConversations.get(conversationId)
      if (!conv?.participants) return {}
      const i = conv.participants.findIndex((p) => p.userId === userId)
      if (i === -1) return {}
      const cur = conv.participants[i].lastDeliveredAt
      if (cur && cur >= lastDeliveredAt) return {} // monotonic
      const nextParts = conv.participants.slice()
      nextParts[i] = { ...nextParts[i], lastDeliveredAt }
      const nextConvs = new Map(prev.wireConversations)
      nextConvs.set(conversationId, { ...conv, participants: nextParts })
      return { wireConversations: nextConvs }
    }),

  seedCursors: (conversationId, cursors) =>
    set((prev) => {
      const conv = prev.wireConversations.get(conversationId)
      if (!conv?.participants) return {}
      const byUser = new Map(cursors.map((c) => [c.userId, c]))
      let changed = false
      const nextParts = conv.participants.map((p) => {
        const c = byUser.get(p.userId)
        if (!c) return p
        const next = { ...p }
        if (c.lastDeliveredAt && (!p.lastDeliveredAt || c.lastDeliveredAt > p.lastDeliveredAt)) {
          next.lastDeliveredAt = c.lastDeliveredAt
          changed = true
        }
        if (c.lastReadAt && (!p.lastReadAt || c.lastReadAt > p.lastReadAt)) {
          next.lastReadAt = c.lastReadAt
          changed = true
        }
        return next
      })
      if (!changed) return {}
      const nextConvs = new Map(prev.wireConversations)
      nextConvs.set(conversationId, { ...conv, participants: nextParts })
      return { wireConversations: nextConvs }
    }),

  markTyping: (conversationId, userId) => {
    // A blocked contact must not be able to animate anything in my UI. The
    // server suppresses their `typing` at the source, but a client that was
    // already subscribed when the block landed can still have one in flight,
    // and the block list is the client's own state — so drop it here too.
    // Presence guards itself the same way (`presence.store.blockedIds`), and
    // reads the same set, so there is one source of truth for "blocked".
    if (usePresenceStore.getState().blockedIds.has(userId)) return
    set((prev) => {
      const existing = prev.typingByConv.get(conversationId) ?? new Set<string>()
      if (existing.has(userId)) return {} // already typing — bump TTL only
      const next = new Set(existing)
      next.add(userId)
      const map = new Map(prev.typingByConv)
      map.set(conversationId, next)
      return { typingByConv: map }
    })
    // Reset the auto-decay timer.
    const key = typingKey(conversationId, userId)
    const prevTimer = typingTimers.get(key)
    if (prevTimer) clearTimeout(prevTimer)
    typingTimers.set(
      key,
      setTimeout(() => {
        typingTimers.delete(key)
        useMessengerStore.setState((prev) => {
          const set_ = prev.typingByConv.get(conversationId)
          if (!set_) return {}
          if (!set_.has(userId)) return {}
          const next = new Set(set_)
          next.delete(userId)
          const map = new Map(prev.typingByConv)
          if (next.size === 0) map.delete(conversationId)
          else map.set(conversationId, next)
          return { typingByConv: map }
        })
      }, TYPING_TTL_MS),
    )
  },

  markRemovedFrom: (conversationId) =>
    set((prev) => {
      if (prev.removedFromConversationIds.has(conversationId)) return {}
      const next = new Set(prev.removedFromConversationIds)
      next.add(conversationId)
      return { removedFromConversationIds: next }
    }),

  clearRemovedFrom: (conversationId) =>
    set((prev) => {
      if (!prev.removedFromConversationIds.has(conversationId)) return {}
      const next = new Set(prev.removedFromConversationIds)
      next.delete(conversationId)
      return { removedFromConversationIds: next }
    }),

  addPendingSend: (msg) =>
    set((prev) => {
      trace('store', `addPendingSend (optimistic)  id=${msg.id.slice(0, 12)} "${(msg.body ?? '').slice(0, 30)}"`)
      const pending = new Map(prev.pendingSends)
      pending.set(msg.id, msg)
      // Also render it in the thread immediately.
      const list = prev.wireMessagesByConv.get(msg.conversationId) ?? []
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(msg.conversationId, [...list, msg])

      // Float this conversation to the top of the list right away (optimistic).
      // The sender's own `message:new` echo carries the same clientMsgId and is
      // deduped by `appendWireMessage` (early-return, no re-sort), so without
      // bumping here the conversation you just messaged in wouldn't move up until
      // reload — even though the server already advanced `last_message_at`.
      const conv = prev.wireConversations.get(msg.conversationId)
      const nextConvs = new Map(prev.wireConversations)
      if (conv) {
        nextConvs.set(msg.conversationId, {
          ...conv,
          lastMessageAt: msg.createdAt,
          lastMessage: previewFromMessage(msg),
        })
      }

      return {
        pendingSends: pending,
        wireMessagesByConv: nextMap,
        wireConversations: nextConvs,
        wireConversationOrder: sortConversationIds(nextConvs),
      }
    }),

  reconcilePendingSend: (tempId, real) =>
    set((prev) => {
      const pending = new Map(prev.pendingSends)
      pending.delete(tempId)
      // Swap the temp message id for the real one in the thread.
      const list = prev.wireMessagesByConv.get(real.conversationId)
      if (!list) return { pendingSends: pending }
      const i = list.findIndex((m) => m.id === tempId)
      const nextList = list.slice()
      if (i !== -1) {
        nextList[i] = real
      } else if (!list.some((m) => m.id === real.id)) {
        // Temp was already dropped — just append the real row.
        nextList.push(real)
      }
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(real.conversationId, nextList)
      return { pendingSends: pending, wireMessagesByConv: nextMap }
    }),

  rollbackPendingSend: (tempId) =>
    set((prev) => {
      const pendingMsg = prev.pendingSends.get(tempId)
      const pending = new Map(prev.pendingSends)
      pending.delete(tempId)
      if (!pendingMsg) return { pendingSends: pending }
      const list = prev.wireMessagesByConv.get(pendingMsg.conversationId) ?? []
      const nextList = list.filter((m) => m.id !== tempId)
      const nextMap = new Map(prev.wireMessagesByConv)
      nextMap.set(pendingMsg.conversationId, nextList)
      return { pendingSends: pending, wireMessagesByConv: nextMap }
    }),

  markBroadcastCopies: (messageIds) =>
    set((prev) => {
      const fresh = messageIds.filter((id) => id && !prev.broadcastCopyIds.has(id))
      if (fresh.length === 0) return {}
      return { broadcastCopyIds: new Set([...prev.broadcastCopyIds, ...fresh]) }
    }),

  resetWire: () => {
    for (const t of typingTimers.values()) clearTimeout(t)
    typingTimers.clear()
    set(initialWire())
  },

  reset: () => {
    for (const t of typingTimers.values()) clearTimeout(t)
    typingTimers.clear()
    set({
      conversations: [],
      calls: [],
      messagesByConversation: {},
      isHydrated: false,
      ...initialWire(),
    })
  },
}))

/** Number of conversations that have ≥1 unread message. Derives from the live
 *  wire state (the raw `s.conversations` field is legacy/empty). */
export const selectTotalUnreadChats = (s: MessengerState, callerId: string | null): number =>
  selectUiConversations(s, callerId).reduce(
    (acc, c) => (c.unreadCount > 0 && !c.isArchived ? acc + 1 : acc),
    0,
  )

/** Aggregate of every unread message across the inbox (wire-derived). */
export const selectTotalUnreadMessages = (s: MessengerState, callerId: string | null): number =>
  selectUiConversations(s, callerId).reduce(
    (acc, c) => (c.isArchived ? acc : acc + c.unreadCount),
    0,
  )

/* ------------------------------------------------------------------ */
/* Wire → UI adapter (W13)                                             */
/* ------------------------------------------------------------------ */

/**
 * Stamp for a chat-list tile: a clock for today, "Yesterday", a date before
 * that. Empty when null (a brand-new conversation with no messages).
 *
 * It used to print the clock whatever the age, so a thread last touched at 6pm
 * three days ago sat in the list reading "06:00 pm" — indistinguishable from
 * one from this evening, and impossible to reconcile with the row above it.
 * The calls feed's `formatCallTime` already draws this distinction.
 */
function formatTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const now = new Date()
  const sameDate = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  if (sameDate(d, now)) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (sameDate(d, yesterday)) return 'Yesterday'
  return d.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function wireParticipantToUi(wp: WireConversationParticipant): Participant {
  return {
    id: wp.userId,
    name: wp.name ?? `User ${wp.userId.slice(0, 8)}`,
    imageUrl: wp.avatarUrl ?? undefined,
    isAdmin: wp.isAdmin,
    // `isOnline` is derived live by `usePresence(member.id)` at render
    // time (see `useConversationOnline` and `MemberOnlineLabel`). The
    // adapter intentionally leaves it undefined — putting a stale value
    // here would defeat the point.
  }
}

/**
 * Convert a `WireConversation` (+ caller id, + optional last message) into
 * the rich `Conversation` shape the existing UI components consume.
 *
 *   * `name` — group: `title`. DM: the other participant's name.
 *   * `lastMessage` / `time` — sourced from the most-recent wire message
 *     loaded for this conversation, falling back to `lastMessageAt`.
 *   * `unreadCount` — heuristic: 1 if a wire message arrived after the
 *     caller's `lastReadAt`, else 0. We don't have a real count yet —
 *     server response could include it later (CDT).
 *   * `isOnline` — placeholder `false` here. Live status is resolved at
 *     render-time by `useConversationOnline(c)` from the presence store,
 *     so any value baked in by the adapter would be stale on every render.
 *
 * Field defaults match the existing UI contract — anything we can't fill
 * gets `false` / `'sent'` / `''` so renderers don't break.
 */
/**
 * Read-receipt reciprocity: while I have read receipts OFF, I don't get to see
 * anyone else's blue ticks either.
 *
 * A module flag rather than a store field because the two places that derive
 * ticks (this file's list adapter and the conversation panel's message adapter)
 * are plain functions, and threading it through both would touch every call
 * site for a value that is global to the session.
 *
 * This is ONLY the half that withholds information from myself. The half that
 * protects other people — my cursor never being published — is enforced
 * server-side and cannot be bypassed from here.
 */
let hideOthersReadReceipts = false

export function setHideOthersReadReceipts(hide: boolean): void {
  if (hideOthersReadReceipts === hide) return
  hideOthersReadReceipts = hide
  // Projections are memoized per conversation; without this the already-cached
  // tiles would keep their old tick until some other field changed.
  uiConversationCache.clear()
}

export function othersReadReceiptsHidden(): boolean {
  return hideOthersReadReceipts
}

/** Label for an attachment-only message, so the tile isn't blank. */
const ATTACHMENT_LABEL: Record<string, string> = {
  photo: 'Photo',
  video: 'Video',
  audio: 'Audio',
  voice: 'Voice message',
  document: 'Document',
}

/** One-line rendering of a system entry's JSON body for a chat-list tile. */
function systemPreviewText(body: string | null): string {
  if (!body) return ''
  let parsed: { event?: string; title?: string | null; previousTitle?: string | null }
  try {
    parsed = JSON.parse(body) as typeof parsed
  } catch {
    // Not the envelope we expect — anything is better than raw JSON, and an
    // empty preview falls back to the conversation title in the tile.
    return ''
  }
  switch (parsed.event) {
    case 'created':        return 'Group created'
    case 'added':          return 'Member added'
    case 'removed':        return 'Member removed'
    case 'left':           return 'Member left'
    case 'renamed':
      // Older entries predate `previousTitle` and only carry the new name.
      return parsed.previousTitle && parsed.title
        ? `Group name changed to "${parsed.title}"`
        : parsed.title ? `Group renamed to "${parsed.title}"` : 'Group renamed'
    case 'photo_changed':  return 'Group photo changed'
    default:               return ''
  }
}

/**
 * Preview line for a chat-list tile, from either source shape: a fully loaded
 * `Message` or the server's `LastMessagePreview`. Falls back to an attachment
 * label when the message carries no text (a photo sent without a caption would
 * otherwise render as an empty row).
 */
function previewText(
  m: WireMessage | LastMessagePreview | undefined,
  callerId?: string | null,
): string {
  if (!m) return ''
  // A deleted last message keeps its tile — the thread still exists and this
  // is still the most recent thing in it — but the tile showed the server's
  // stored body, which for a deleted row is the lowercase placeholder. Same
  // wording as the bubble, and the same rule about who deleted it.
  const deletedAt = 'deletedAt' in m ? m.deletedAt : null
  if (deletedAt) {
    const senderId = 'senderId' in m ? m.senderId : null
    return senderId && callerId && senderId === callerId
      ? 'You deleted this message'
      : 'This message was deleted'
  }
  // A system entry's body is a JSON envelope, not prose — a new group's tile
  // read `{"title":"New","event":"created","actorId":"14708…`. The thread
  // renders these through `buildSystemText`; the tile needs one short line and
  // no names, so it gets its own rendering rather than that one's.
  if (m.type === 'system') return systemPreviewText(m.body)
  if (m.body) return m.body
  const kind =
    'attachmentKind' in m ? m.attachmentKind : (m.attachments?.[0]?.kind ?? null)
  return kind ? (ATTACHMENT_LABEL[kind] ?? 'Attachment') : ''
}

function wireToUiConversation(
  c: WireConversation,
  callerId: string | null,
  lastMessage: WireMessage | undefined,
  unreadCountOverride?: number,
  favorite = false,
): Conversation {
  const me = c.participants?.find((p) => p.userId === callerId)
  const other = c.participants?.find((p) => p.userId !== callerId)

  const name =
    c.kind === 'group'
      ? (c.title ?? 'Group')
      : (other?.name ?? `User ${other?.userId.slice(0, 8) ?? '?'}`)

  // Preview source: whichever of the two is actually the newest message.
  //
  // The loaded thread used to win outright. But a thread is seeded once and
  // then only grows while its channel is joined, whereas `c.lastMessage` is
  // refreshed by the server on every conversation update — so any thread the
  // session opened earlier pinned its tile to the message it happened to end
  // on. The row kept an old preview and an old time while `sortConversationIds`
  // went on ordering the list by the real `lastMessageAt`, which is why the
  // times down the list read out of order.
  //
  // When both point at the SAME message the thread copy still wins: it has
  // live edits and deletes applied, which the snapshot does not.
  const snapshot = c.lastMessage ?? undefined
  const preview =
    !lastMessage ? snapshot
    : !snapshot ? lastMessage
    : snapshot.id === lastMessage.id ? lastMessage
    : snapshot.createdAt > lastMessage.createdAt ? snapshot
    : lastMessage
  const lastMsgText = previewText(preview, callerId)
  const lastMsgTimeIso = preview?.createdAt ?? c.lastMessageAt

  // Unread heuristic — true if the freshest wire activity post-dates my
  // last-read cursor. Without a real per-message read map we can't show
  // an exact count, so this collapses to 0 or 1.
  const lastActivity = lastMsgTimeIso ?? c.createdAt
  const myLastRead = me?.lastReadAt
  const hasUnread =
    !!lastActivity && (!myLastRead || lastActivity > myLastRead) && preview?.senderId !== callerId

  // Tick status for MY last message, from the other participants' cursors:
  //   all others readAt  >= msg → read      (✓✓ blue)
  //   all others delivAt >= msg → delivered  (✓✓ grey)
  //   else                       → sent      (✓)
  // Only meaningful when the last message is mine (the tile shows the icon only
  // when isSentByMe). `c` gets a fresh ref on each cursor update (applyReadUpdate
  // / applyDeliveredUpdate), so this recomputes + the tile re-renders live.
  let status: MessageStatus = 'sent'
  if (preview?.deletedAt) {
    status = 'read'
  } else if (preview && preview.senderId === callerId) {
    const at = preview.createdAt
    const others = c.participants?.filter((p) => p.userId !== callerId) ?? []
    if (others.length > 0) {
      const allRead = others.every((p) => p.lastReadAt != null && p.lastReadAt >= at)
      const allDelivered = others.every((p) => {
        const d = p.lastDeliveredAt ?? p.lastReadAt
        return d != null && d >= at
      })
      // Reciprocity: with my own receipts off, "read" collapses to "delivered".
      status =
        allRead && !hideOthersReadReceipts
          ? 'read'
          : allDelivered || allRead
            ? 'delivered'
            : 'sent'
    }
  }

  return {
    id: c.id,
    name,
    lastMessage: lastMsgText,
    time: formatTime(lastMsgTimeIso),
    isOnline: false,
    unreadCount: unreadCountOverride ?? (hasUnread ? 1 : 0),
    isSentByMe: preview?.senderId === callerId,
    messageStatus: status,
    imageUrl: c.kind === 'group' ? (c.avatarUrl ?? undefined) : other?.avatarUrl ?? undefined,
    isGroup: c.kind === 'group',
    isMuted: me?.muted ?? false,
    isPinned: me?.pinned ?? false,
    isArchived: me?.archived ?? false,
    isFavorite: favorite,
    participants: c.participants?.map(wireParticipantToUi),
  }
}

/**
 * Per-conversation projection cache so identical inputs yield the **same
 * object reference** across renders. Without this, `selectUiConversations`
 * would build fresh `Conversation` objects on every call — `useShallow`
 * sees ref-different elements and treats the whole array as changed,
 * triggering downstream effects in a tight loop.
 *
 * Invalidates by identity of the underlying refs (wire conv, last
 * message) + caller id. Since the store reassigns these refs only on
 * real mutations, this cache hits whenever wire data is unchanged.
 */
interface UiConversationCacheEntry {
  c: WireConversation
  lastMessage: WireMessage | undefined
  callerId: string | null
  unreadCount: number | undefined
  favorite: boolean
  ui: Conversation
}
const uiConversationCache = new Map<string, UiConversationCacheEntry>()

function wireToUiCached(
  c: WireConversation,
  callerId: string | null,
  lastMessage: WireMessage | undefined,
  unreadCount: number | undefined,
  favorite: boolean,
): Conversation {
  const hit = uiConversationCache.get(c.id)
  if (
    hit &&
    hit.c === c &&
    hit.lastMessage === lastMessage &&
    hit.callerId === callerId &&
    hit.unreadCount === unreadCount &&
    hit.favorite === favorite
  ) {
    // Same inputs → SAME object reference. Critical: `useShallow` consumers
    // loop forever if this returns a fresh object each call (React #185).
    return hit.ui
  }
  const ui = wireToUiConversation(c, callerId, lastMessage, unreadCount, favorite)
  uiConversationCache.set(c.id, { c, lastMessage, callerId, unreadCount, favorite, ui })
  return ui
}

/**
 * Selector: project wire state into the existing UI Conversation[] shape.
 * Drop into a component that previously read `s.conversations`:
 *
 *   const conversations = useMessengerStore(
 *     useShallow((s) => selectUiConversations(s, myUserId)),
 *   )
 *
 * `useShallow` is the right pair — per-conversation refs are kept stable
 * by the cache above, so the array contents compare shallow-equal until
 * something actually changes.
 */
export function selectUiConversations(s: MessengerState, callerId: string | null): Conversation[] {
  return s.wireConversationOrder
    .map((id) => s.wireConversations.get(id))
    .filter((c): c is WireConversation => c !== undefined)
    // Hide EMPTY DMs — a DM created by opening a contact from search but never
    // messaged. Without this, a search-without-send clutters the list (and
    // reappears on refresh). Groups always show; a DM shows the moment it has a
    // message (optimistic send bumps lastMessageAt / adds to the thread).
    .filter((c) => {
      if (c.kind !== 'dm') return true
      return !!c.lastMessageAt || (s.wireMessagesByConv.get(c.id)?.length ?? 0) > 0
    })
    .map((c) => {
      const thread = s.wireMessagesByConv.get(c.id)
      const lastMessage = thread && thread.length > 0 ? thread[thread.length - 1] : undefined
      // Real unread count from the loaded thread (live messages accumulate here
      // via the app-wide subscription + local cache). undefined = "no thread" →
      // wireToUiConversation falls back to its 0/1 heuristic; unmapped (my own
      // participant row isn't in `c.participants`) → 0, see `unreadCountFor`.
      // Passing the count through wireToUiCached keeps the object reference
      // STABLE across renders (fresh objects here would loop useShallow
      // forever, React #185).
      const count = unreadCountFor(thread, c.participants, callerId)
      return wireToUiCached(c, callerId, lastMessage, count, s.favoriteIds.has(c.id))
    })
}

/* ----------------------------------------------------------------------
 * Calls feed (W14) — wire → UI adapter + ref-stable selector
 * --------------------------------------------------------------------*/

function formatCallTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const now = new Date()
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (sameDay) return `Today, ${time}`
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) return `Yesterday, ${time}`
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + `, ${time}`
}

function formatCallDuration(ms: number | undefined): string | undefined {
  if (typeof ms !== 'number' || ms <= 0) return undefined
  const totalSec = Math.round(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  if (m === 0) return `${s} sec`
  if (s === 0) return `${m} min`
  return `${m} min ${s} sec`
}

interface CallPayloadShape {
  direction?: 'in' | 'out'
  durationMs?: number
  missed?: boolean
  /** Forward-compatible — calling stack may add `kind: 'audio' | 'video'`. */
  kind?: 'audio' | 'video'
  [k: string]: unknown
}

function wireCallToCallRecord(
  msg: WireMessage,
  conv: WireConversation | undefined,
  callerId: string | null,
): CallRecord {
  const isGroup = conv?.kind === 'group'
  const other = conv?.participants?.find((p) => p.userId !== callerId)

  const name = isGroup
    ? (conv?.title ?? 'Group call')
    : (other?.name ?? `User ${other?.userId.slice(0, 8) ?? '?'}`)

  const payload = (msg.callPayload ?? undefined) as CallPayloadShape | undefined
  // Direction derived from senderId for reliability — the call-lifecycle
  // writer may not set `direction` consistently across SFU vs PSTN paths.
  const callDir: 'in' | 'out' = msg.senderId === callerId ? 'out' : 'in'
  const isMissedCall = payload?.missed === true
  const isVideoCall = payload?.kind === 'video'

  return {
    id: msg.id,
    name,
    imageUrl: isGroup ? undefined : other?.avatarUrl ?? undefined,
    isVideoCall,
    isMissedCall,
    callDir,
    time: formatCallTime(msg.createdAt),
    duration: formatCallDuration(payload?.durationMs),
    // Peer's contactPresenceId for call-back (1:1 only).
    peerId: isGroup ? undefined : other?.userId,
  }
}

interface UiCallCacheEntry {
  msg: WireMessage
  conv: WireConversation | undefined
  callerId: string | null
  ui: CallRecord
}
const uiCallCache = new Map<string, UiCallCacheEntry>()

/**
 * Selector: project the wire calls feed into the existing UI CallRecord[]
 * shape the calls-tab consumes. Ref-stable per `(msg, conv, callerId)` so
 * `useShallow` works.
 */
export function selectUiCallRecords(s: MessengerState, callerId: string | null): CallRecord[] {
  return s.wireCalls.map((msg) => {
    const conv = s.wireConversations.get(msg.conversationId)
    const hit = uiCallCache.get(msg.id)
    if (hit && hit.msg === msg && hit.conv === conv && hit.callerId === callerId) {
      return hit.ui
    }
    const ui = wireCallToCallRecord(msg, conv, callerId)
    uiCallCache.set(msg.id, { msg, conv, callerId, ui })
    return ui
  })
}
